import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Lock, Sparkles, ShieldCheck, ArrowRight } from 'lucide-react';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';

// Map landing-page plan keys ("pro", "standard", "enterprise") to backend
// PLAN_PRICES_USD keys.  Kept identical on purpose — if you rename plans, only
// change in one place here + PLAN_PRICES_USD.
const PAYABLE_PLANS = ['standard', 'pro', 'enterprise'];

/**
 * Public `/subscribe?plan=pro` route:
 *  - If the visitor is not logged in, pushes them into Emergent Google OAuth
 *    with a redirect back to `/subscribe?plan=pro` so we resume the flow.
 *  - Once authenticated, POSTs /api/billing/checkout and hard-redirects to the
 *    Stripe Checkout URL.
 *  - Enterprise is "contact sales" (no self-serve price) — we just show an
 *    email mailto.
 */
export default function Subscribe() {
  const [params] = useSearchParams();
  const plan = params.get('plan');
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!plan || !PAYABLE_PLANS.includes(plan)) {
      setErr('Unknown plan.');
      return;
    }
    // Enterprise uses contact-sales (no self-serve Stripe price)
    if (plan === 'enterprise') return;

    // Not logged in → push through Google OAuth, coming back here
    if (!user) {
      const ret = `${window.location.origin}/subscribe?plan=${plan}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(ret)}`;
      return;
    }

    // Logged in → create checkout, redirect to Stripe
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const r = await axios.post(
          `${API}/billing/checkout`,
          { plan_key: plan, origin_url: window.location.origin },
          { withCredentials: true }
        );
        if (cancelled) return;
        if (r.data?.url) {
          window.location.href = r.data.url; // Stripe-hosted checkout
        } else {
          setErr('Stripe did not return a checkout URL.');
        }
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not start Stripe checkout.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [plan, user, authLoading]);

  // Enterprise path → contact sales
  if (plan === 'enterprise') {
    return (
      <Wrap>
        <Sparkles className="w-10 h-10 text-amber-400 mb-4" />
        <h1 className="text-3xl font-bold text-white">Enterprise plan</h1>
        <p className="text-slate-300 mt-2 max-w-md">Enterprise is quoted per-portfolio with custom SLAs, on-prem options, SSO, and a dedicated CSM. Reach out and we'll come back within 1 business day.</p>
        <a href="mailto:sales@fmflexipro.app?subject=Enterprise%20Plan%20Enquiry" data-testid="enterprise-contact-link" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-500 text-black font-semibold hover:bg-amber-400">
          Contact sales <ArrowRight className="w-4 h-4" />
        </a>
        <button onClick={() => navigate('/')} className="mt-3 text-slate-400 text-sm underline">Back to home</button>
      </Wrap>
    );
  }

  return (
    <Wrap>
      {err ? (
        <>
          <div className="text-rose-400 text-sm mb-4">{err}</div>
          <Button onClick={() => navigate('/')} variant="outline" className="border-white/20 text-white hover:bg-white/10">Back to home</Button>
        </>
      ) : !user ? (
        <>
          <Lock className="w-10 h-10 text-teal-400 mb-4" />
          <h1 className="text-2xl font-bold text-white">Redirecting to sign-in…</h1>
          <p className="text-slate-400 mt-2">You'll sign in with Google, then continue straight to Stripe to complete payment.</p>
          <Loader2 className="w-5 h-5 text-teal-400 animate-spin mt-6" />
        </>
      ) : (
        <>
          <ShieldCheck className="w-10 h-10 text-teal-400 mb-4" />
          <h1 className="text-2xl font-bold text-white">Opening secure checkout…</h1>
          <p className="text-slate-400 mt-2">Redirecting to Stripe for the <strong className="text-white uppercase">{plan}</strong> plan.</p>
          <Loader2 className="w-5 h-5 text-teal-400 animate-spin mt-6" />
          {busy && <div className="text-xs text-muted-foreground mt-2">If the browser does not redirect in a few seconds, please disable pop-up blockers and retry.</div>}
        </>
      )}
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#0f1729]/80 backdrop-blur-sm p-6 sm:p-10 flex flex-col items-start">
        {children}
      </div>
    </div>
  );
}
